<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import {
  getBanks,
  checkAccountNumber as apiCheckAccountNumber,
  getRecipients,
  createRecipient,
  deleteRecipient as apiDeleteRecipient,
  getSingleTransferAdminFee,
  createSingleTransfer,
  getBalance,
  sendOtp,
  verifyOtp,
} from "@/api/transfer";

const store = useStore();
const router = useRouter();

const activeTab = ref("bank");
const recipient = ref(null);
const amount = ref("");
const notes = ref("");
const showRecipientModal = ref(false);
const showAddNewModal = ref(false);

// Add New form state
const banks = ref([]);
const newBank = ref("");
const newAccountNumber = ref("");
const newAccountName = ref("");
const isCheckingAccount = ref(false);
const checkError = ref("");
const checkSuccess = ref(false);
const showBankDropdown = ref(false);
const bankSearch = ref("");
const newBankId = ref(null);

const tabs = [
  { key: "bank", label: "Bank" },
  // { key: "virtual_account", label: "Virtual Account" },
  { key: "ewallet", label: "E-Wallet" },
];

const savedDestinations = ref([]);
const isLoadingRecipients = ref(false);
const recipientsError = ref("");

const adminFee = ref(0);
const isLoadingAdminFee = ref(false);
const adminFeeError = ref("");

const isCreatingTransfer = ref(false);
const createError = ref("");
const showReviewModal = ref(false);
const reviewData = ref(null);

const showPaymentStep = ref(false);
const localAvailableBalance = ref(0);
const isLoadingBalance = ref(false);
const selectedPaymentMethod = ref("balance");
const isSendingOtp = ref(false);

const showOtpModal = ref(false);
const otpDigits = ref(["", "", "", "", "", ""]);
const otpInputs = ref([]);
const maskedEmail = ref("");
const paymentTransactionId = ref("");
const paymentTransferUuid = ref("");
const paymentMethodValue = ref("");
const paymentBankId = ref(null);
const isVerifyingOtp = ref(false);
const isResendingOtp = ref(false);
const resendCooldown = ref(0);
const otpError = ref("");
let otpCooldownTimer = null;

const isOtpComplete = computed(() =>
  otpDigits.value.every((digit) => digit !== "" && /^[0-9]$/.test(digit))
);
const combinedOtp = computed(() => otpDigits.value.join(""));

const isBiUser = computed(() => {
  const profile = store.getters["auth/userProfile"];
  return profile?.is_bi === true || profile?.is_bi === 1;
});

const virtualAccounts = ref([
  {
    id: 1,
    name: "Permata Virtual Account",
    logo: require("@/assets/img/logos/bank-permata.png"),
  },
]);

const paymentTotal = computed(() => Number(reviewData.value?.total_amount || 0));

const hasSufficientBalance = computed(() => {
  if (isLoadingBalance.value) return true;
  return normalizeIndonesianNumber(localAvailableBalance.value) >= paymentTotal.value;
});

const accountTypeMap = { bank: "bank", virtual_account: "va", ewallet: "ewallet" };

async function fetchRecipients() {
  isLoadingRecipients.value = true;
  recipientsError.value = "";
  try {
    const accountType = accountTypeMap[activeTab.value];
    const response = await getRecipients(accountType);
    if (response?.status === "success" && Array.isArray(response.data)) {
      savedDestinations.value = response.data.map((r) => ({
        id: r.id,
        name: r.account_name,
        accountNumber: r.account_number,
        bank: r.bank?.bank_name ?? "-",
        bankId: r.bank_id ?? r.bank?.id ?? null,
        bankColor: "#f0f2f5",
        bankTextColor: "#0d2b3e",
      }));
    }
  } catch {
    recipientsError.value = "Failed to load recipients.";
  } finally {
    isLoadingRecipients.value = false;
  }
}

const transferAmount = computed(() => {
  const num = parseInt(amount.value.replace(/\D/g, ""), 10);
  return isNaN(num) ? 0 : num;
});

const formattedAmount = computed(() => {
  if (!amount.value) return "0";
  return transferAmount.value.toLocaleString("id-ID");
});

const totalAmount = computed(() => transferAmount.value + adminFee.value);

const formattedAdminFee = computed(() => adminFee.value.toLocaleString("id-ID"));

const formattedTotal = computed(() => totalAmount.value.toLocaleString("id-ID"));

const recipientLabel = computed(() => {
  if (activeTab.value === "virtual_account") return "Choose from Saved VA";
  if (activeTab.value === "ewallet") return "Choose from Saved E-Wallet";
  return "Choose Recipient";
});

const modalTitle = computed(() => {
  if (activeTab.value === "virtual_account") return "Choose from Saved VA";
  if (activeTab.value === "ewallet") return "Choose from Saved E-Wallet";
  return "Choose Recipient";
});

const modalSubtitle = computed(() => {
  if (activeTab.value === "virtual_account") return "You can choose the saved virtual account or add a new one";
  if (activeTab.value === "ewallet") return "You can choose the saved e-wallet or add a new one";
  return "You can choose the saved recipient or add new one";
});

const canContinue = computed(() => {
  const raw = parseInt(amount.value.replace(/\D/g, ""), 10);
  return recipient.value !== null && raw > 0;
});

function onAmountInput(e) {
  const raw = e.target.value.replace(/\D/g, "");
  amount.value = raw;
}

function chooseRecipient() {
  showRecipientModal.value = true;
  fetchRecipients();
}

function closeModal() {
  showRecipientModal.value = false;
}

async function openAddNew() {
  newBank.value = "";
  newBankId.value = null;
  newAccountNumber.value = "";
  newAccountName.value = "";
  checkError.value = "";
  checkSuccess.value = false;
  showBankDropdown.value = false;
  showAddNewModal.value = true;
  // Reload banks based on current active tab
  try {
    const isEwallet = activeTab.value === "ewallet";
    const response = await getBanks(isEwallet);
    if (response?.status === "success" && Array.isArray(response.data)) {
      banks.value = response.data.filter((b) => b.bank_name !== "Others Bank");
    }
  } catch {
    // keep existing banks list
  }
}

function closeAddNew() {
  showAddNewModal.value = false;
}

function selectBank(bankName, bankId) {
  newBank.value = bankName;
  newBankId.value = bankId;
  showBankDropdown.value = false;
  bankSearch.value = "";
  newAccountName.value = "";
  checkError.value = "";
  checkSuccess.value = false;
}

async function checkAccount() {
  if (!newBank.value || !newAccountNumber.value) return;
  isCheckingAccount.value = true;
  checkError.value = "";
  checkSuccess.value = false;
  newAccountName.value = "";
  try {
    const formData = new FormData();
    formData.append("bank", newBank.value);
    formData.append("account_number", newAccountNumber.value);
    const response = await apiCheckAccountNumber(formData);
    if (response && response.status === "success" && response.data) {
      newAccountName.value = response.data.account_name;
      checkSuccess.value = true;
    } else {
      checkError.value = response?.message || "Account not found. Please check the details.";
    }
  } catch {
    checkError.value = "Failed to verify account. Please try again.";
  } finally {
    isCheckingAccount.value = false;
  }
}

const isSavingRecipient = ref(false);
const saveError = ref("");

async function confirmRecipient() {
  isSavingRecipient.value = true;
  saveError.value = "";
  try {
    const accountTypeMap = { bank: "bank", virtual_account: "va", ewallet: "ewallet" };
    await createRecipient({
      bank_id: newBankId.value,
      account_number: newAccountNumber.value,
      account_name: newAccountName.value,
      account_type: accountTypeMap[activeTab.value],
    });
    recipient.value = {
      id: null,
      name: newAccountName.value,
      accountNumber: newAccountNumber.value,
      bank: newBank.value,
      bankId: newBankId.value,
      bankColor: "#f0f2f5",
      bankTextColor: "#0d2b3e",
    };
    await fetchAdminFee(newBankId.value);
    await fetchRecipients();
    showAddNewModal.value = false;
    showRecipientModal.value = false;
  } catch {
    saveError.value = "Failed to save recipient. Please try again.";
  } finally {
    isSavingRecipient.value = false;
  }
}

const canCheck = computed(() => newBank.value !== "" && newAccountNumber.value.trim() !== "");

const addNewProviderLabel = computed(() => {
  if (activeTab.value === "virtual_account") return "Virtual Account";
  if (activeTab.value === "ewallet") return "E-Wallet Provider";
  return "Bank";
});

const addNewProviderPlaceholder = computed(() => {
  if (activeTab.value === "virtual_account") return "Choose Virtual Account";
  if (activeTab.value === "ewallet") return "Choose E-Wallet Provider";
  return "Choose Destination Bank";
});

const addNewAccountLabel = computed(() => {
  if (activeTab.value === "ewallet") return "Phone Number";
  return "Account Number";
});

const accountCardStyle = computed(() => ({
  backgroundImage: `url(${process.env.BASE_URL}assets/bg-account-card.png)`,
  backgroundSize: "cover",
  backgroundPosition: "center",
}));

const addNewAccountPlaceholder = computed(() => {
  if (activeTab.value === "ewallet") return "0812xxxxxxxx";
  return "Enter Account Number";
});

const filteredBanks = computed(() => {
  if (!bankSearch.value.trim()) return banks.value;
  return banks.value.filter((b) =>
    b.bank_name.toLowerCase().includes(bankSearch.value.toLowerCase())
  );
});

async function fetchAdminFee(bankId) {
  if (!bankId) {
    adminFee.value = 0;
    return;
  }
  isLoadingAdminFee.value = true;
  adminFeeError.value = "";
  try {
    const response = await getSingleTransferAdminFee(bankId);
    if (response?.status === "success" && response.data) {
      adminFee.value = Number(response.data.admin_fee) || 0;
    } else {
      adminFee.value = 0;
      adminFeeError.value = response?.message || "Failed to load admin fee.";
    }
  } catch {
    adminFee.value = 0;
    adminFeeError.value = "Failed to load admin fee.";
  } finally {
    isLoadingAdminFee.value = false;
  }
}

function resetAdminFee() {
  adminFee.value = 0;
  adminFeeError.value = "";
  isLoadingAdminFee.value = false;
}

function selectRecipient(dest) {
  recipient.value = dest;
  showRecipientModal.value = false;
  fetchAdminFee(dest.bankId);
}

async function deleteDestination(id) {
  try {
    await apiDeleteRecipient(id);
    savedDestinations.value = savedDestinations.value.filter((d) => d.id !== id);
    if (recipient.value?.id === id) {
      recipient.value = null;
      resetAdminFee();
    }
  } catch {
    // keep list intact if delete fails
  }
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString("id-ID");
}

function getBankBadgeLabel(bankName) {
  if (!bankName) return "—";
  const short = bankName.replace(/^Bank\s+/i, "").trim();
  return short.length <= 4 ? short.toUpperCase() : short.slice(0, 4).toUpperCase();
}

function getBankBadgeStyle(bankName) {
  const name = (bankName || "").toLowerCase();
  if (name.includes("bca")) return { backgroundColor: "#00529c", color: "#ffffff" };
  if (name.includes("permata")) return { backgroundColor: "#00a651", color: "#ffffff" };
  if (name.includes("mandiri")) return { backgroundColor: "#003d79", color: "#ffffff" };
  if (name.includes("bni")) return { backgroundColor: "#f37021", color: "#ffffff" };
  return { backgroundColor: "#f0f2f5", color: "#0d2b3e" };
}

async function onContinue() {
  if (!canContinue.value || isCreatingTransfer.value) return;

  isCreatingTransfer.value = true;
  createError.value = "";
  try {
    const payload = {
      bank_id: recipient.value.bankId,
      account_number: recipient.value.accountNumber,
      account_name: recipient.value.name,
      amount: transferAmount.value,
    };
    if (notes.value.trim()) {
      payload.notes = notes.value.trim();
    }

    const response = await createSingleTransfer(payload);
    if (response?.status === "success" && response.data) {
      reviewData.value = response.data;
      showReviewModal.value = true;
    } else {
      createError.value = response?.message || "Failed to create transfer.";
    }
  } catch {
    createError.value = "Failed to create transfer. Please try again.";
  } finally {
    isCreatingTransfer.value = false;
  }
}

function normalizeIndonesianNumber(value) {
  if (typeof value === "string") {
    value = value.replace(/\./g, "").replace(",", ".");
  }
  return Number(value);
}

function selectPaymentMethod(methodId) {
  if (isLoadingBalance.value && methodId === "balance") return;
  selectedPaymentMethod.value = methodId;
}

async function loadPaymentBalance() {
  selectedPaymentMethod.value = isBiUser.value ? 1 : "balance";

  if (isBiUser.value) {
    isLoadingBalance.value = false;
    localAvailableBalance.value = 0;
    return;
  }

  isLoadingBalance.value = true;
  try {
    const balanceResponse = await getBalance();
    if (
      balanceResponse?.status === "success" &&
      balanceResponse.data?.merchant
    ) {
      localAvailableBalance.value =
        normalizeIndonesianNumber(balanceResponse.data.merchant.balance) || 0;
    } else {
      localAvailableBalance.value = 0;
    }
  } catch {
    localAvailableBalance.value = 0;
  } finally {
    isLoadingBalance.value = false;
  }
}

function onContinueToPayment() {
  if (!reviewData.value) return;
  showReviewModal.value = false;
  showPaymentStep.value = true;
  loadPaymentBalance();
}

function goBackFromPayment() {
  showPaymentStep.value = false;
  showReviewModal.value = true;
}

function buildPaymentFormData() {
  const formData = new FormData();
  formData.append("transaction_id", paymentTransactionId.value);
  formData.append("method", paymentMethodValue.value);
  if (paymentMethodValue.value === "virtual_account" && paymentBankId.value) {
    formData.append("bank_id", paymentBankId.value);
  }
  return formData;
}

function resetOtpDigits() {
  otpDigits.value = ["", "", "", "", "", ""];
  otpError.value = "";
}

function closeOtpModal() {
  showOtpModal.value = false;
  resetOtpDigits();
}

function handleOtpInput(event, index) {
  const value = event.target.value;
  if (value.length === 1 && /^[0-9]$/.test(value)) {
    otpDigits.value[index] = value;
    if (index < otpDigits.value.length - 1) {
      otpInputs.value[index + 1]?.focus();
    }
  } else if (value.length === 0) {
    otpDigits.value[index] = "";
  } else {
    event.target.value = otpDigits.value[index];
  }
}

function handleOtpKeyDown(event, index) {
  if (event.key === "Backspace") {
    if (otpDigits.value[index] === "" && index > 0) {
      otpInputs.value[index - 1]?.focus();
    }
    otpDigits.value[index] = "";
  } else if (event.key === "ArrowLeft" && index > 0) {
    otpInputs.value[index - 1]?.focus();
  } else if (event.key === "ArrowRight" && index < otpDigits.value.length - 1) {
    otpInputs.value[index + 1]?.focus();
  }
}

function handleOtpPaste(event) {
  event.preventDefault();
  const pastedText = (event.clipboardData || window.clipboardData).getData("text").trim();
  const sixDigits = pastedText.replace(/\D/g, "").slice(0, 6);
  if (!sixDigits.length) return;

  for (let i = 0; i < sixDigits.length && i < otpDigits.value.length; i++) {
    otpDigits.value[i] = sixDigits[i];
  }
  const nextFocusIndex =
    sixDigits.length < otpDigits.value.length ? sixDigits.length : otpDigits.value.length - 1;
  otpInputs.value[nextFocusIndex]?.focus();
}

function startOtpResendCooldown() {
  resendCooldown.value = 60;
  if (otpCooldownTimer) clearInterval(otpCooldownTimer);
  otpCooldownTimer = setInterval(() => {
    resendCooldown.value -= 1;
    if (resendCooldown.value <= 0) {
      clearInterval(otpCooldownTimer);
      otpCooldownTimer = null;
    }
  }, 1000);
}

async function resendOtpHandler() {
  if (isResendingOtp.value || resendCooldown.value > 0 || !paymentTransactionId.value) return;

  isResendingOtp.value = true;
  otpError.value = "";
  try {
    const response = await sendOtp(buildPaymentFormData());
    if (response?.status === "success") {
      maskedEmail.value = response.data?.masked_email || maskedEmail.value;
      startOtpResendCooldown();
    } else {
      otpError.value = response?.message || "Failed to resend OTP. Please try again.";
    }
  } catch {
    otpError.value = "Failed to resend OTP. Please try again.";
  } finally {
    isResendingOtp.value = false;
  }
}

async function verifyOtpHandler() {
  if (!isOtpComplete.value || isVerifyingOtp.value || !paymentTransactionId.value) return;

  isVerifyingOtp.value = true;
  otpError.value = "";
  try {
    const formData = new FormData();
    formData.append("otp", combinedOtp.value);
    formData.append("transaction_id", paymentTransactionId.value);

    const response = await verifyOtp(formData);
    if (response?.status === "success") {
      showOtpModal.value = false;

      if (paymentMethodValue.value === "balance") {
        router.push({
          name: "SingleTransferPaymentSuccess",
          state: {
            transactionId:
              response.data?.payment?.transaction_id ||
              reviewData.value?.transaction_id ||
              paymentTransactionId.value,
            paymentDate: response.data?.payment_date || new Date().toISOString(),
            amount: reviewData.value?.amount || response.data?.payment?.amount || 0,
            adminFee: reviewData.value?.admin_fee || response.data?.admin_fee || 0,
            totalAmount:
              reviewData.value?.total_amount ||
              response.data?.payment?.amount ||
              0,
          },
        });
      } else if (paymentMethodValue.value === "virtual_account") {
        const vaData = response.data || {};
        const paymentDataForNextPage = {
          transactionId: vaData.transaction_id || paymentTransactionId.value,
          virtualAccountNumber: vaData.va_number || "",
          totalAmount: vaData.amount || reviewData.value?.total_amount || 0,
          accountName: vaData.merchant_name || "PT Anugrah Multi Remittance",
          bankName: vaData.bank_name || "",
          batchId: paymentTransferUuid.value || null,
        };

        router.push({
          name: "VirtualAccountDisplay",
          state: {
            paymentDetails: paymentDataForNextPage,
            expiresAt: vaData.expires_at,
            source: "single_transfer",
            amount: reviewData.value?.amount || 0,
            adminFee: reviewData.value?.admin_fee || 0,
          },
        });
      }
    } else {
      otpError.value = response?.message || "Invalid OTP. Please try again.";
      resetOtpDigits();
      await nextTick();
      otpInputs.value[0]?.focus();
    }
  } catch (error) {
    otpError.value =
      error?.response?.data?.message || "Invalid OTP or an error occurred. Please try again.";
    resetOtpDigits();
    await nextTick();
    otpInputs.value[0]?.focus();
  } finally {
    isVerifyingOtp.value = false;
  }
}

watch(isOtpComplete, (complete) => {
  if (complete && showOtpModal.value && !isVerifyingOtp.value) {
    verifyOtpHandler();
  }
});

async function handleConfirmTransfer() {
  if (!selectedPaymentMethod.value) {
    Swal.fire("Error", "Please select a payment method.", "error");
    return;
  }

  if (selectedPaymentMethod.value === "balance" && !hasSufficientBalance.value) {
    Swal.fire(
      "Error",
      "Insufficient balance to make this payment. Please choose another payment method or top up your balance.",
      "error"
    );
    return;
  }

  const transactionId = reviewData.value?.transaction_id;
  const transferUuid = reviewData.value?.uuid;
  if (!transactionId) {
    Swal.fire("Error", "Transaction ID is missing. Cannot proceed with payment.", "error");
    return;
  }

  isSendingOtp.value = true;
  const formData = new FormData();
  formData.append("transaction_id", transactionId);

  let methodValue = "";
  let bankIdentifier = null;

  if (selectedPaymentMethod.value === "balance") {
    methodValue = "balance";
  } else {
    methodValue = "virtual_account";
    const selectedVA = virtualAccounts.value.find(
      (va) => va.id === selectedPaymentMethod.value
    );
    if (selectedVA) {
      bankIdentifier = selectedVA.id;
      formData.append("bank_id", bankIdentifier);
    } else {
      Swal.fire("Error", "Selected virtual account details not found.", "error");
      isSendingOtp.value = false;
      return;
    }
  }
  formData.append("method", methodValue);

  try {
    const otpResponse = await sendOtp(formData);
    if (otpResponse?.status === "success") {
      paymentTransactionId.value = transactionId;
      paymentTransferUuid.value = transferUuid || "";
      paymentMethodValue.value = methodValue;
      paymentBankId.value = bankIdentifier;
      maskedEmail.value = otpResponse.data?.masked_email || "";
      resetOtpDigits();
      showOtpModal.value = true;
      await nextTick();
      otpInputs.value[0]?.focus();
    } else {
      Swal.fire(
        "Error",
        otpResponse?.message || "Failed to initiate payment and send OTP. Please try again.",
        "error"
      );
    }
  } catch {
    Swal.fire("Error", "An error occurred while trying to initiate payment.", "error");
  } finally {
    isSendingOtp.value = false;
  }
}

watch(activeTab, () => {
  recipient.value = null;
  resetAdminFee();
});

onMounted(async () => {
  store.commit("setShowNavbar", false);
  store.commit("setShowSidenav", false);
  store.state.showTransferNavbar = true;
  try {
    const isEwallet = activeTab.value === "ewallet";
    const response = await getBanks(isEwallet);
    if (response?.status === "success" && Array.isArray(response.data)) {
      banks.value = response.data.filter((b) => b.bank_name !== "Others Bank");
    }
  } catch {
    // banks stays empty, fallback to empty list
  }
});

onBeforeUnmount(() => {
  if (otpCooldownTimer) {
    clearInterval(otpCooldownTimer);
    otpCooldownTimer = null;
  }
  store.commit("setShowNavbar", true);
  store.commit("setShowSidenav", true);
  store.state.showTransferNavbar = false;
});
</script>

<template>
  <!-- Payment Step -->
  <div v-if="showPaymentStep && reviewData" class="st-page">
    <div class="st-container st-container--payment">
      <button class="st-back" type="button" @click="goBackFromPayment">
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 1L1 7l6 6" stroke="#3D5A6C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Back
      </button>

      <h1 class="st-title">Payment</h1>

      <div class="st-payment-total-card">
        <span class="st-payment-total-label">Total</span>
        <span class="st-payment-total-value">IDR {{ formatCurrency(reviewData.total_amount) }}</span>
      </div>

      <p class="st-payment-section-title">Choose Payment Methods</p>

      <div class="st-payment-methods-card">
        <template v-if="!isBiUser">
          <div class="st-payment-group-header">
            <span class="st-payment-group-label">Available Balance</span>
            <span class="st-quickest-badge">Quickest Transaction</span>
          </div>
          <button
            type="button"
            class="st-payment-option"
            :class="{
              'st-payment-option--selected': selectedPaymentMethod === 'balance',
              'st-payment-option--insufficient':
                selectedPaymentMethod === 'balance' && !hasSufficientBalance && !isLoadingBalance,
              'st-payment-option--disabled': isLoadingBalance,
            }"
            :disabled="isLoadingBalance"
            @click="selectPaymentMethod('balance')"
          >
            <span class="st-payment-radio" :class="{ 'st-payment-radio--checked': selectedPaymentMethod === 'balance' }">
              <span v-if="selectedPaymentMethod === 'balance'" class="st-payment-radio__dot"></span>
            </span>
            <span class="st-payment-option__text">
              <template v-if="isLoadingBalance">Loading balance...</template>
              <template v-else>
                IDR {{ formatCurrency(localAvailableBalance) }}
                <span
                  v-if="!hasSufficientBalance && selectedPaymentMethod === 'balance'"
                  class="st-payment-insufficient"
                >
                  Insufficient Balance
                </span>
              </template>
            </span>
          </button>
        </template>

        <p class="st-payment-group-title" :class="{ 'st-payment-group-title--spaced': !isBiUser }">
          Transfer Virtual Account
        </p>

        <button
          v-for="va in virtualAccounts"
          :key="va.id"
          type="button"
          class="st-payment-option st-payment-option--va"
          :class="{ 'st-payment-option--selected': selectedPaymentMethod === va.id }"
          @click="selectPaymentMethod(va.id)"
        >
          <span class="st-payment-radio" :class="{ 'st-payment-radio--checked': selectedPaymentMethod === va.id }">
            <span v-if="selectedPaymentMethod === va.id" class="st-payment-radio__dot"></span>
          </span>
          <span class="st-payment-option__va-row">
            <img :src="va.logo" :alt="va.name" class="st-payment-va-logo" />
            <span>{{ va.name }}</span>
          </span>
        </button>
      </div>

      <button
        class="st-continue st-continue--active st-payment-confirm"
        :disabled="isSendingOtp"
        @click="handleConfirmTransfer"
      >
        {{ isSendingOtp ? "Processing..." : "Confirm Transfer" }}
      </button>
    </div>
  </div>

  <!-- Main Form -->
  <div v-else class="st-page">
    <div class="st-container">

      <!-- Title -->
      <h1 class="st-title">Single Transfer</h1>

      <!-- Tab Bar -->
      <div class="st-tab-bar">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="st-tab"
          :class="{ 'st-tab--active': activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- Choose Recipient -->
      <button class="st-card st-recipient" @click="chooseRecipient">
        <div class="st-recipient__left">
          <svg class="st-recipient__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="8" r="4" stroke="#3D5A6C" stroke-width="1.5"/>
            <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="#3D5A6C" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          <span class="st-recipient__text" :class="{ 'st-recipient__text--filled': recipient }">
            {{ recipient ? recipient.name : recipientLabel }}
          </span>
        </div>
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1l6 6-6 6" stroke="#3D5A6C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- Amount -->
      <div class="st-card st-amount">
        <span class="st-label">Amount</span>
        <div class="st-amount__row">
          <span class="st-amount__currency">IDR</span>
          <input
            class="st-amount__input"
            :class="{ 'st-amount__input--empty': !amount }"
            type="text"
            inputmode="numeric"
            :value="formattedAmount"
            @input="onAmountInput"
            placeholder="0"
          />
        </div>
      </div>

      <!-- Notes -->
      <div class="st-card st-notes">
        <span class="st-label">Notes (Optional)</span>
        <textarea
          class="st-notes__textarea"
          v-model="notes"
          placeholder="Write your notes here..."
          rows="3"
        ></textarea>
      </div>

      <!-- Admin Fee & Total -->
      <div class="st-summary">
        <div class="st-summary-card">
          <div class="st-summary-card__row">
            <span class="st-summary-card__label">Admin Fee</span>
            <span v-if="isLoadingAdminFee" class="st-fee-badge st-fee-badge--loading">
              <span class="st-fee-spinner"></span>
            </span>
            <span v-else class="st-fee-badge">IDR {{ formattedAdminFee }}</span>
          </div>
          <p v-if="adminFeeError" class="st-summary-card__error">{{ adminFeeError }}</p>
          <div class="st-summary-card__delivery">
            <span class="st-delivery-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.5 1.5L4 9h4.5L6.5 14.5L12 7H7.5L9.5 1.5Z" fill="#F5A623" stroke="#F5A623" stroke-width="0.5" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="st-summary-card__delivery-text">Money will arrive in minutes</span>
          </div>
        </div>

        <div class="st-total">
          <span class="st-total__label">Total</span>
          <span class="st-total__value">IDR {{ formattedTotal }}</span>
        </div>
      </div>

      <!-- Continue Button -->
      <p v-if="createError" class="st-create-error">{{ createError }}</p>
      <button
        class="st-continue"
        :class="{ 'st-continue--active': canContinue && !isCreatingTransfer }"
        :disabled="!canContinue || isCreatingTransfer"
        @click="onContinue"
      >
        {{ isCreatingTransfer ? "Processing..." : "Continue" }}
      </button>

    </div>
  </div>

  <!-- Recipient Modal -->
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="showRecipientModal" class="modal-overlay" @click.self="closeModal">
        <Transition name="modal-slide">
          <div v-if="showRecipientModal" class="modal-sheet">

            <!-- Modal Header -->
            <div class="modal-header">
              <div class="modal-header__text">
                <h2 class="modal-title">{{ modalTitle }}</h2>
                <p class="modal-subtitle">{{ modalSubtitle }}</p>
              </div>
              <button class="modal-close" @click="closeModal">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1l16 16M17 1L1 17" stroke="#3D5A6C" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>

            <!-- Add New Recipient -->
            <button class="modal-add-btn" @click="openAddNew">
              <span class="modal-add-btn__icon">+</span>
              <span class="modal-add-btn__label">Add New Recipient</span>
            </button>

            <div class="modal-divider"></div>

            <!-- Saved Destination -->
            <p class="modal-section-label">Saved Destination</p>

            <div class="modal-list">
              <!-- Loading state -->
              <div v-if="isLoadingRecipients" class="modal-empty">
                <span class="an-spinner" style="border-color: rgba(67,187,117,0.3); border-top-color: #43bb75; width:22px; height:22px; display:inline-block;"></span>
              </div>

              <!-- Error state -->
              <div v-else-if="recipientsError" class="modal-empty" style="color:#c53030">
                {{ recipientsError }}
              </div>

              <!-- List -->
              <template v-else>
                <button
                  v-for="dest in savedDestinations"
                  :key="dest.id"
                  class="modal-dest-item"
                  @click="selectRecipient(dest)"
                >
                  <div class="bank-badge" :style="{ backgroundColor: dest.bankColor, color: dest.bankTextColor }">
                    {{ dest.bank.substring(4, 15) }}
                  </div>
                  <div class="modal-dest-info">
                    <span class="modal-dest-name">{{ dest.name }}</span>
                    <span class="modal-dest-account">{{ dest.accountNumber }}</span>
                  </div>
                  <button class="modal-dest-delete" @click.stop="deleteDestination(dest.id)" title="Delete">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="#9eadb7" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M10 11v6M14 11v6" stroke="#9eadb7" stroke-width="1.5" stroke-linecap="round"/>
                    </svg>
                  </button>
                </button>

                <!-- Empty state -->
                <div v-if="savedDestinations.length === 0" class="modal-empty">
                  No saved destinations yet.
                </div>
              </template>
            </div>

          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>

  <!-- Add New Modal -->
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="showAddNewModal" class="modal-overlay" style="z-index: 2100" @click.self="closeAddNew">
        <Transition name="modal-slide">
          <div v-if="showAddNewModal" class="modal-sheet an-sheet">

            <!-- Header -->
            <div class="modal-header">
              <h2 class="modal-title">Add New</h2>
              <button class="modal-close" @click="closeAddNew">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M1 1l16 16M17 1L1 17" stroke="#3D5A6C" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>

            <!-- Provider Field (Bank / Virtual Account / E-Wallet) -->
            <div class="an-field">
              <label class="an-label">{{ addNewProviderLabel }}<span class="an-required">*</span></label>
              <div class="an-dropdown-wrapper" @click.stop>
                <button
                  class="an-select"
                  :class="{ 'an-select--filled': newBank }"
                  @click="showBankDropdown = !showBankDropdown"
                  type="button"
                >
                  <span>{{ newBank || addNewProviderPlaceholder }}</span>
                  <svg width="14" height="8" viewBox="0 0 14 8" fill="none">
                    <path d="M1 1l6 6 6-6" stroke="#7a8b95" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div v-if="showBankDropdown" class="an-dropdown-list">
                  <div class="an-dropdown-search">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="7" stroke="#9eadb7" stroke-width="2"/>
                      <path d="M16.5 16.5l4 4" stroke="#9eadb7" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                    <input
                      class="an-dropdown-search-input"
                      v-model="bankSearch"
                      placeholder="Search..."
                      @click.stop
                      autofocus
                    />
                  </div>
                  <div class="an-dropdown-items">
                    <button
                      v-for="bank in filteredBanks"
                      :key="bank.bank_name"
                      class="an-dropdown-item"
                      :class="{ 'an-dropdown-item--active': newBank === bank.bank_name }"
                      @click="selectBank(bank.bank_name, bank.id)"
                      type="button"
                    >
                      {{ bank.bank_name }}
                    </button>
                    <div v-if="filteredBanks.length === 0 && banks.length > 0" class="an-dropdown-empty">Not found.</div>
                    <div v-if="banks.length === 0" class="an-dropdown-empty">Loading...</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Account Number / Phone Number Field -->
            <div class="an-field">
              <label class="an-label">{{ addNewAccountLabel }}<span class="an-required">*</span></label>
              <input
                class="an-input"
                type="text"
                inputmode="numeric"
                v-model="newAccountNumber"
                :placeholder="addNewAccountPlaceholder"
                @input="checkSuccess = false; checkError = ''; newAccountName = ''"
              />
            </div>

            <!-- Result: account name card -->
            <div v-if="checkSuccess && newAccountName" class="an-account-card" :style="accountCardStyle">
              <div class="an-account-card__inner">
                <span class="an-account-card__label">Account Name</span>
                <span class="an-account-card__name">{{ newAccountName }}</span>
              </div>
              <div class="an-account-card__circle"></div>
            </div>
            <div v-if="checkError" class="an-result-error">{{ checkError }}</div>

            <!-- Spacer -->
            <div class="an-spacer"></div>

            <!-- Check / Continue Button -->
            <button
              v-if="!checkSuccess"
              class="an-check-btn"
              :class="{ 'an-check-btn--active': canCheck && !isCheckingAccount }"
              :disabled="!canCheck || isCheckingAccount"
              @click="checkAccount"
            >
              <span v-if="isCheckingAccount" class="an-spinner"></span>
              <span>{{ isCheckingAccount ? "Checking..." : "Check" }}</span>
            </button>
            <div v-if="saveError" class="an-result-error">{{ saveError }}</div>
            <button
              v-if="checkSuccess"
              class="an-check-btn an-check-btn--active"
              :disabled="isSavingRecipient"
              @click="confirmRecipient"
            >
              <span v-if="isSavingRecipient" class="an-spinner"></span>
              <span>{{ isSavingRecipient ? "Saving..." : "Continue" }}</span>
            </button>

          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>

  <!-- Review Transfer Modal -->
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="showReviewModal && reviewData" class="modal-overlay review-overlay">
        <Transition name="modal-slide">
          <div v-if="showReviewModal && reviewData" class="review-card">
            <h2 class="review-title">Review transfer</h2>
            <p class="review-subtitle">Please review all the details before continue</p>

            <div class="review-recipient-box">
              <span class="review-recipient-label">Recipient</span>
              <div class="review-recipient-row">
                <div
                  class="bank-badge review-bank-badge"
                  :style="getBankBadgeStyle(reviewData.bank_name)"
                >
                  {{ getBankBadgeLabel(reviewData.bank_name) }}
                </div>
                <div class="review-recipient-info">
                  <p class="review-recipient-name">{{ reviewData.account_name }}</p>
                  <p class="review-recipient-account">{{ reviewData.account_number }}</p>
                </div>
              </div>
            </div>

            <div class="review-rows">
              <div class="review-row">
                <span class="review-row__label">Sent Amount</span>
                <span class="review-row__value">IDR {{ formatCurrency(reviewData.amount) }}</span>
              </div>
              <div class="review-row">
                <span class="review-row__label">Admin Fee</span>
                <span class="review-row__value">IDR {{ formatCurrency(reviewData.admin_fee) }}</span>
              </div>
              <div class="review-divider"></div>
              <div class="review-row review-row--total">
                <span class="review-row__label">Total Amount</span>
                <span class="review-row__value review-row__value--total">
                  IDR {{ formatCurrency(reviewData.total_amount) }}
                </span>
              </div>
            </div>

            <button
              class="st-continue st-continue--active review-continue-btn"
              @click="onContinueToPayment"
            >
              Continue to Payment
            </button>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>

  <!-- OTP Modal -->
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="showOtpModal" class="modal-overlay otp-overlay">
        <Transition name="modal-slide">
          <div v-if="showOtpModal" class="otp-card">
            <button class="otp-close" type="button" aria-label="Close" @click="closeOtpModal">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M1 1l16 16M17 1L1 17" stroke="#3D5A6C" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>

            <h2 class="otp-title">Enter your OTP</h2>
            <p class="otp-subtitle">
              We have sent an 6 digit OTP to your email at
              <strong>{{ maskedEmail }}</strong>
            </p>

            <div class="otp-inputs">
              <input
                v-for="(digit, index) in otpDigits"
                :key="index"
                :ref="(el) => (otpInputs[index] = el)"
                v-model="otpDigits[index]"
                type="text"
                inputmode="numeric"
                maxlength="1"
                class="otp-input"
                :disabled="isVerifyingOtp"
                @input="(event) => handleOtpInput(event, index)"
                @keydown="(event) => handleOtpKeyDown(event, index)"
                @paste="index === 0 ? handleOtpPaste($event) : null"
              />
            </div>

            <p v-if="isVerifyingOtp" class="otp-verifying">Verifying OTP...</p>
            <p v-if="otpError" class="otp-error">{{ otpError }}</p>

            <p class="otp-resend">
              Didn't receive an OTP?
              <button
                type="button"
                class="otp-resend__btn"
                :class="{ 'otp-resend__btn--disabled': isResendingOtp || resendCooldown > 0 }"
                :disabled="isResendingOtp || resendCooldown > 0"
                @click="resendOtpHandler"
              >
                Resend<span v-if="resendCooldown > 0"> ({{ resendCooldown }}s)</span>
              </button>
            </p>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* ── Page ───────────────────────────────────────────── */
.st-page {
  min-height: 100vh;
  background-color: #f0f2f5;
  padding: 80px 0 80px;
}

.st-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.st-title {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-weight: 700;
  font-size: 28px;
  color: #0d2b3e;
  margin: 0 0 4px;
  line-height: 1.2;
}

/* ── Tab Bar ────────────────────────────────────────── */
.st-tab-bar {
  display: flex;
  background-color: #d8dde3;
  border-radius: 100px;
  padding: 4px;
  gap: 2px;
}

.st-tab {
  flex: 1;
  padding: 10px 8px;
  border: none;
  border-radius: 100px;
  background: transparent;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #7a8b95;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.st-tab--active {
  background-color: #ffffff;
  color: #0d2b3e;
  font-weight: 600;
  border: 2px solid #1a73e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

/* ── Shared Card ────────────────────────────────────── */
.st-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

/* ── Recipient Button ───────────────────────────────── */
.st-recipient {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  width: 100%;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease;
}

.st-recipient:hover {
  background-color: #f8fafb;
}

.st-recipient__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.st-recipient__icon { flex-shrink: 0; }

.st-recipient__text {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #0d2b3e;
}

/* ── Amount ─────────────────────────────────────────── */
.st-amount {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.st-label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #7a8b95;
}

.st-amount__row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.st-amount__currency {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #0d2b3e;
  flex-shrink: 0;
}

.st-amount__input {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 28px;
  font-weight: 400;
  color: #0d2b3e;
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  min-width: 0;
  caret-color: #1a73e8;
}

.st-amount__input--empty { color: #9eadb7; }
.st-amount__input::placeholder { color: #9eadb7; }

/* ── Notes ──────────────────────────────────────────── */
.st-notes {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.st-notes__textarea {
  width: 100%;
  padding: 12px 16px;
  background-color: #f0f2f5;
  border: none;
  border-radius: 10px;
  resize: none;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  color: #0d2b3e;
  outline: none;
  transition: background-color 0.15s ease;
  box-sizing: border-box;
}

.st-notes__textarea::placeholder { color: #9eadb7; }
.st-notes__textarea:focus { background-color: #e8ecf0; }

/* ── Admin Fee & Total ──────────────────────────────── */
.st-summary {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 4px;
}

.st-summary-card {
  background: #ffffff;
  border: 1px solid #e2e8ee;
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.st-summary-card__row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.st-summary-card__label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #7a8b95;
}

.st-fee-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background-color: #e8f0fe;
  border-radius: 100px;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #1a73e8;
  line-height: 1.2;
}

.st-fee-badge--loading {
  min-width: 72px;
  justify-content: center;
}

.st-fee-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(26, 115, 232, 0.25);
  border-top-color: #1a73e8;
  border-radius: 50%;
  animation: st-fee-spin 0.7s linear infinite;
}

@keyframes st-fee-spin {
  to { transform: rotate(360deg); }
}

.st-summary-card__error {
  margin: -6px 0 0;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 12px;
  color: #c53030;
}

.st-summary-card__delivery {
  display: flex;
  align-items: center;
  gap: 10px;
}

.st-delivery-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #fff8e6;
  flex-shrink: 0;
}

.st-summary-card__delivery-text {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #0d2b3e;
  line-height: 1.3;
}

.st-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
}

.st-total__label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #7a8b95;
}

.st-total__value {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #0d2b3e;
  line-height: 1.2;
}

/* ── Continue Button ────────────────────────────────── */
.st-continue {
  width: 100%;
  padding: 16px;
  border: none;
  border-radius: 100px;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: not-allowed;
  transition: all 0.2s ease;
  background-color: #d8dde3;
  color: #7a8b95;
  margin-top: 0;
}

.st-continue--active {
  background-color: #43bb75;
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(67, 187, 117, 0.3);
}

.st-continue--active:hover { background-color: #37a865; }

.st-create-error {
  margin: 0;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 13px;
  color: #c53030;
  text-align: center;
}

/* ── Review Modal ───────────────────────────────────── */
.review-overlay {
  background: rgba(240, 242, 245, 0.92);
}

.review-card {
  background: #ffffff;
  width: 100%;
  max-width: 520px;
  border-radius: 24px;
  padding: 32px 28px;
  box-shadow: 0 8px 32px rgba(13, 43, 62, 0.08);
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.review-title {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-weight: 700;
  font-size: 28px;
  color: #0d2b3e;
  margin: 0;
  line-height: 1.2;
}

.review-subtitle {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #7a8b95;
  margin: -12px 0 0;
  line-height: 1.4;
}

.review-recipient-box {
  border: 1px solid #e2e8ee;
  border-radius: 16px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.review-recipient-label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #7a8b95;
}

.review-recipient-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.review-bank-badge {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  font-size: 10px;
}

.review-recipient-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.review-recipient-name {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #0d2b3e;
  margin: 0;
  line-height: 1.3;
}

.review-recipient-account {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #7a8b95;
  margin: 0;
  line-height: 1.3;
}

.review-rows {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.review-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.review-row__label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #7a8b95;
}

.review-row__value {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #0d2b3e;
  text-align: right;
}

.review-divider {
  border: none;
  border-top: 1px dashed #d8dde3;
  margin: 2px 0;
}

.review-row--total {
  align-items: flex-end;
  margin-top: 4px;
}

.review-row__value--total {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}

.review-continue-btn {
  margin-top: 8px;
}

/* ── Payment Step ───────────────────────────────────── */
.st-container--payment {
  gap: 20px;
}

.st-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  padding: 0;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #3d5a6c;
  cursor: pointer;
  margin-bottom: 4px;
}

.st-back:hover {
  color: #0d2b3e;
}

.st-payment-total-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border: 1px solid #e2e8ee;
  border-radius: 16px;
  padding: 20px 22px;
}

.st-payment-total-label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #7a8b95;
}

.st-payment-total-value {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #0d2b3e;
  line-height: 1.2;
}

.st-payment-section-title {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #0d2b3e;
  margin: 4px 0 0;
}

.st-payment-methods-card {
  background: #ffffff;
  border: 1px solid #e2e8ee;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.st-payment-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.st-payment-group-label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #0d2b3e;
}

.st-quickest-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background-color: #e8f0fe;
  border-radius: 100px;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #1a73e8;
}

.st-payment-group-title {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #0d2b3e;
  margin: 0;
}

.st-payment-group-title--spaced {
  margin-top: 8px;
}

.st-payment-option {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px 18px;
  background: #ffffff;
  border: 1.5px solid #e2e8ee;
  border-radius: 14px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.st-payment-option:hover:not(:disabled) {
  border-color: #1a73e8;
}

.st-payment-option--selected {
  border-color: #1a73e8;
  background-color: #f0f6ff;
  box-shadow: 0 0 0 1px rgba(26, 115, 232, 0.15);
}

.st-payment-option--insufficient {
  border-color: #fb6340;
  background-color: #fff5f2;
}

.st-payment-option--disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.st-payment-radio {
  width: 20px;
  height: 20px;
  border: 2px solid #c5d0d8;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s ease;
}

.st-payment-radio--checked {
  border-color: #1a73e8;
}

.st-payment-radio__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #1a73e8;
}

.st-payment-option__text {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #0d2b3e;
  line-height: 1.3;
}

.st-payment-insufficient {
  display: block;
  margin-top: 2px;
  font-size: 13px;
  font-weight: 500;
  color: #fb6340;
}

.st-payment-option__va-row {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #0d2b3e;
}

.st-payment-va-logo {
  height: 24px;
  width: auto;
  object-fit: contain;
}

.st-payment-confirm {
  margin-top: 8px;
}

/* ── OTP Modal ──────────────────────────────────────── */
.otp-overlay {
  z-index: 2200;
}

.otp-card {
  position: relative;
  background: #ffffff;
  width: 100%;
  max-width: 480px;
  border-radius: 20px;
  padding: 36px 32px 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  text-align: center;
}

.otp-close {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.15s ease;
}

.otp-close:hover {
  background-color: #f0f2f5;
}

.otp-title {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #0d2b3e;
  margin: 0 0 12px;
  line-height: 1.2;
}

.otp-subtitle {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #7a8b95;
  margin: 0 0 28px;
  line-height: 1.5;
}

.otp-subtitle strong {
  color: #0d2b3e;
  font-weight: 600;
}

.otp-inputs {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 24px;
}

.otp-input {
  width: 48px;
  height: 52px;
  border: 1.5px solid #e2e8ee;
  border-radius: 12px;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 22px;
  font-weight: 600;
  color: #0d2b3e;
  text-align: center;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  caret-color: #1a73e8;
}

.otp-input:focus {
  border-color: #1a73e8;
  box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.15);
}

.otp-input:disabled {
  background-color: #f8fafb;
  opacity: 0.7;
}

.otp-verifying {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  color: #7a8b95;
  margin: -8px 0 12px;
}

.otp-error {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 13px;
  color: #c53030;
  margin: -8px 0 12px;
}

.otp-resend {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  color: #7a8b95;
  margin: 0;
}

.otp-resend__btn {
  border: none;
  background: transparent;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  font-weight: 600;
  color: #43bb75;
  cursor: pointer;
}

.otp-resend__btn:hover:not(:disabled) {
  color: #37a865;
  text-decoration: underline;
}

.otp-resend__btn--disabled,
.otp-resend__btn:disabled {
  color: #9eadb7;
  cursor: not-allowed;
  text-decoration: none;
}

/* ── Modal Overlay ──────────────────────────────────── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.modal-sheet {
  background: #ffffff;
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  border-radius: 24px;
  padding: 28px 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

/* ── Modal Header ───────────────────────────────────── */
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
}

.modal-title {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-weight: 700;
  font-size: 22px;
  color: #0d2b3e;
  margin: 0 0 6px;
  line-height: 1.2;
}

.modal-subtitle {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 13px;
  color: #7a8b95;
  margin: 0;
  line-height: 1.5;
}

.modal-close {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease;
}

.modal-close:hover { background-color: #e2e6ea; }

/* ── Add New Button ─────────────────────────────────── */
.modal-add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px;
  background: #ffffff;
  border: 1.5px solid #d8dde3;
  border-radius: 100px;
  cursor: pointer;
  margin-bottom: 20px;
  transition: background-color 0.15s ease;
}

.modal-add-btn:hover { background-color: #f8fafb; }

.modal-add-btn__icon {
  font-size: 20px;
  font-weight: 400;
  color: #0d2b3e;
  line-height: 1;
}

.modal-add-btn__label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #0d2b3e;
}

/* ── Divider & Section Label ────────────────────────── */
.modal-divider {
  height: 1px;
  background-color: #e9ecef;
  margin-bottom: 16px;
}

.modal-section-label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #7a8b95;
  margin: 0 0 12px;
}

/* ── Destination List ───────────────────────────────── */
.modal-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  flex: 1;
  padding-bottom: 4px;
}

.modal-dest-item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  background: #ffffff;
  border: 1.5px solid #e9ecef;
  border-radius: 14px;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.modal-dest-item:hover {
  background-color: #f0faf5;
  border-color: #43bb75;
}

/* ── Bank Badge ─────────────────────────────────────── */
.bank-badge {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}

/* ── Dest Info ──────────────────────────────────────── */
.modal-dest-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
}

.modal-dest-name {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #0d2b3e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modal-dest-account {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 13px;
  color: #7a8b95;
}

/* ── Delete Button ──────────────────────────────────── */
.modal-dest-delete {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  flex-shrink: 0;
  transition: background-color 0.15s ease;
}

.modal-dest-delete:hover { background-color: #fff0f0; }
.modal-dest-delete:hover svg path { stroke: #e53e3e; }

/* ── Empty State ────────────────────────────────────── */
.modal-empty {
  text-align: center;
  padding: 32px 0;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  color: #9eadb7;
}

/* ── Transitions ────────────────────────────────────── */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.modal-slide-enter-from,
.modal-slide-leave-to {
  transform: scale(0.95);
  opacity: 0;
}

/* ── Add New Modal ──────────────────────────────────── */
.an-sheet {
  display: flex;
  flex-direction: column;
  min-height: 420px;
}

.an-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.an-label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #3d5a6c;
}

.an-required {
  color: #e53e3e;
  margin-left: 2px;
}

/* Bank dropdown */
.an-dropdown-wrapper {
  position: relative;
}

.an-select {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #ffffff;
  border: 1.5px solid #d8dde3;
  border-radius: 12px;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 15px;
  color: #9eadb7;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease;
}

.an-select--filled {
  color: #0d2b3e;
}

.an-select:focus,
.an-select:hover {
  border-color: #43bb75;
  outline: none;
}

.an-dropdown-list {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1.5px solid #d8dde3;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  z-index: 10;
  overflow: hidden;
}

.an-dropdown-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid #e9ecef;
  position: sticky;
  top: 0;
  background: #ffffff;
}

.an-dropdown-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  color: #0d2b3e;
  background: transparent;
}

.an-dropdown-search-input::placeholder {
  color: #9eadb7;
}

.an-dropdown-items {
  max-height: 200px;
  overflow-y: auto;
}

.an-dropdown-item {
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  color: #0d2b3e;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.an-dropdown-item:hover {
  background-color: #f0faf5;
}

.an-dropdown-item--active {
  background-color: #e6f7ef;
  color: #2d7a57;
  font-weight: 600;
}

.an-dropdown-empty {
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: #9eadb7;
}

/* Account number input */
.an-input {
  width: 100%;
  padding: 14px 16px;
  background: #ffffff;
  border: 1.5px solid #d8dde3;
  border-radius: 12px;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 15px;
  color: #0d2b3e;
  outline: none;
  transition: border-color 0.15s ease;
  box-sizing: border-box;
}

.an-input::placeholder {
  color: #9eadb7;
}

.an-input:focus {
  border-color: #43bb75;
}

/* Phone number input wrapper */
.an-input-wrapper {
  display: flex;
  align-items: center;
}

.an-input-wrapper--phone {
  border: 1.5px solid #d8dde3;
  border-radius: 12px;
  overflow: hidden;
  transition: border-color 0.15s ease;
}

.an-input-wrapper--phone:focus-within {
  border-color: #43bb75;
}

.an-phone-prefix {
  padding: 14px 12px 14px 16px;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #0d2b3e;
  background: #f0f2f5;
  border-right: 1.5px solid #d8dde3;
  white-space: nowrap;
  user-select: none;
}

.an-input--with-prefix {
  border: none !important;
  border-radius: 0 !important;
  flex: 1;
}

.an-input--with-prefix:focus {
  border-color: transparent !important;
}

/* Account name result card */
.an-account-card {
  position: relative;
  border-radius: 16px;
  padding: 20px 24px;
  overflow: hidden;
  margin-bottom: 4px;
}

.an-account-card__inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.an-account-card__label {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.65);
}

.an-account-card__name {
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
}

.an-account-card__circle {
  position: absolute;
  right: -30px;
  top: -30px;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 28px solid rgba(67, 187, 117, 0.15);
  pointer-events: none;
}

/* Result messages */
.an-result-error {
  padding: 10px 14px;
  background: #fff0f0;
  border-radius: 10px;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 13px;
  color: #c53030;
  margin-bottom: 8px;
}

/* Spacer pushes Check button to bottom */
.an-spacer {
  flex: 1;
  min-height: 24px;
}

/* Check button */
.an-check-btn {
  width: 100%;
  padding: 16px;
  border: none;
  border-radius: 100px;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: not-allowed;
  background-color: #d8dde3;
  color: #7a8b95;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  margin-top: auto;
}

.an-check-btn--active {
  background-color: #43bb75;
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(67, 187, 117, 0.3);
}

.an-check-btn--active:hover {
  background-color: #37a865;
}

.an-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
